import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import ArrowRightLine from '@/assets/icons/arrow_right_line.svg?component';
import { Button } from '@/components/atoms/Buttons';
import SkeletonBox from '@/components/atoms/SkeletonBox/SkeletonBox';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import ContentBlock from '@/components/molecules/ContentBlock';
import { EmptyState } from '@/components/molecules/EmptyState';
import type { Image } from '@/components/organisms/ImageViewer';
import type { RetryProps } from '@/components/retryFailedQueries';
import SomethingHappened from '@/components/SomethingHappened';

import styles from './LatestImageCard.module.scss';

export interface LatestImageCardProps {
    /** Already translated. */
    title: string;
    /** Already translated. Describes the photo for screen readers. */
    imageAlt: string;
    /** The newest is shown. */
    images?: Image[];
    /** The request for the images, not the image itself. */
    isLoading: boolean;
    /**
     * Set when the request for the images failed: the error state with Try again, which a pen
     * without detections must not be told it is.
     */
    loadError?: RetryProps | null;
    /** The page with every image, reached through the card's "View" action. */
    url: string;
    /**
     * The image scales to the height the card leaves it, so the caller gives the card a
     * bounded height — see `CageLiceDashboard.module.scss`'s `.topBlock`.
     */
    className?: string;
}

/**
 * The most recent detection image, with a way through to the page that holds them all.
 *
 * The card's own header carries the title and the action, so the card has one header.
 */
export default function LatestImageCard(props: LatestImageCardProps) {
    const { title, imageAlt, images, isLoading, loadError, url, className } = props;
    const { t } = useTranslation();
    const navigate = useNavigate();

    const path = images?.at(-1)?.path;
    const [loadedPath, setLoadedPath] = useState<string | null>(null);
    const [failedPath, setFailedPath] = useState<string | null>(null);

    const hasImage = path !== undefined;
    const hasLoaded = hasImage && loadedPath === path;
    const hasFailed = hasImage && failedPath === path;

    const showSkeleton = !loadError && (isLoading || (hasImage && !hasLoaded && !hasFailed));

    return (
        <ContentBlock
            title={title}
            fill
            className={className}
            headerActions={
                <Button
                    variant={'secondary'}
                    trackingId="latest_image_view"
                    onClick={() => navigate(url)}
                    iconEnd={
                        <SvgIcon
                            color="currentColor"
                            icon={ArrowRightLine}
                            size="custom"
                            customSize={20}
                        />
                    }>
                    {t('common.actions.view')}
                </Button>
            }>
            <div className={styles.imageArea}>
                {hasImage && !hasFailed && !loadError && (
                    <>
                        <img
                            className={styles.backdrop}
                            src={path}
                            alt=""
                            aria-hidden="true"
                            data-ready={hasLoaded}
                        />
                        <img
                            className={styles.image}
                            src={path}
                            alt={imageAlt}
                            data-ready={hasLoaded}
                            onLoad={() => setLoadedPath(path)}
                            onError={() => setFailedPath(path)}
                        />
                    </>
                )}

                {showSkeleton && <SkeletonBox className={styles.overlay} borderRadius={8} />}

                {!isLoading && hasFailed && (
                    <p className={styles.message} role="status">
                        {t('organisms.latestImageCard.couldNotLoadTheImage')}
                    </p>
                )}

                {loadError && <SomethingHappened {...loadError} />}

                {/* No image at all is a statement about the pen, not about the request. */}
                {!isLoading && !loadError && !hasImage && (
                    <EmptyState title={t('common.labels.atTheMomentWeDo')} />
                )}
            </div>
        </ContentBlock>
    );
}
